import { useState } from "react";

export interface Vehicle {
  id: string;
  plate: string;
  make: string;
  model: string;
  color: string;
}

interface VehicleCardProps {
  vehicle: Vehicle;
  isSelected: boolean;
  onSelect: () => void;
  onEdit: () => void;
  onDelete: () => void;
}

export default function VehicleCard({ vehicle, isSelected, onSelect, onEdit, onDelete }: VehicleCardProps) {
  const [showMenu, setShowMenu] = useState(false);

  return (
    <div style={{ position: "relative" }}>
      <div
        onClick={onSelect}
        style={{
          padding: "16px 20px",
          borderRadius: "12px",
          border: isSelected ? "2px solid var(--blue-500)" : "1px solid var(--line)",
          background: isSelected ? "rgba(59, 130, 246, 0.04)" : "var(--white)",
          cursor: "pointer",
          transition: "all 0.15s ease",
          boxShadow: isSelected ? "0 0 0 3px rgba(59, 130, 246, 0.1)" : "0 1px 2px rgba(0, 0, 0, 0.05)",
        }}
        onMouseEnter={(e) => {
          if (!isSelected) {
            e.currentTarget.style.borderColor = "var(--blue-300)";
            e.currentTarget.style.boxShadow = "0 2px 8px rgba(0, 0, 0, 0.08)";
          }
        }}
        onMouseLeave={(e) => {
          if (!isSelected) {
            e.currentTarget.style.borderColor = "var(--line)";
            e.currentTarget.style.boxShadow = "0 1px 2px rgba(0, 0, 0, 0.05)";
          }
        }}
      >
        {/* Top row: Icon + Plate badge + Menu */}
        <div style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "12px",
        }}>
          {/* Car icon + Plate */}
          <div style={{
            display: "flex",
            alignItems: "center",
            gap: "12px",
          }}>
            {/* Car icon */}
            <div style={{
              width: "32px",
              height: "32px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "var(--stone-100)",
              borderRadius: "8px",
              color: "var(--stone-600)",
            }}>
              <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                <path d="M19 17h2c.6 0 1-.4 1-1v-3c0-.9-.7-1.6-1.5-1.6H6.5l-1-1.5H5c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h3.5L10 7.5h4l1.5-1.5H19c1.1 0 2 .9 2 2v5c0 .6-.4 1-1 1h-2" />
                <circle cx="7" cy="13" r="1.5" />
                <circle cx="17" cy="13" r="1.5" />
              </svg>
            </div>

            {/* License plate badge */}
            <div style={{
              background: "var(--stone-800)",
              color: "var(--white)",
              padding: "6px 12px",
              borderRadius: "6px",
              fontFamily: "var(--font-mono)",
              fontSize: "14px",
              fontWeight: 600,
              letterSpacing: "0.05em",
            }}>
              {vehicle.plate.toUpperCase()}
            </div>
          </div>

          {/* Action menu */}
          <div style={{ position: "relative" }}>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowMenu(!showMenu);
              }}
              style={{
                width: "32px",
                height: "32px",
                borderRadius: "8px",
                border: "none",
                background: "transparent",
                color: "var(--stone-400)",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: 0,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "var(--stone-100)";
                e.currentTarget.style.color = "var(--ink)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "transparent";
                e.currentTarget.style.color = "var(--stone-400)";
              }}
            >
              <svg width={16} height={16} viewBox="0 0 24 24" fill="currentColor">
                <circle cx="12" cy="5" r="2" />
                <circle cx="12" cy="12" r="2" />
                <circle cx="12" cy="19" r="2" />
              </svg>
            </button>

            {/* Dropdown menu */}
            {showMenu && (
              <div style={{
                position: "absolute",
                top: "100%",
                right: 0,
                marginTop: "4px",
                background: "var(--white)",
                border: "1px solid var(--line)",
                borderRadius: "8px",
                boxShadow: "var(--shadow-lg)",
                minWidth: "140px",
                zIndex: 100,
              }}>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowMenu(false);
                    onEdit();
                  }}
                  style={{
                    width: "100%",
                    padding: "10px 16px",
                    border: "none",
                    background: "transparent",
                    textAlign: "left",
                    fontSize: "14px",
                    color: "var(--ink)",
                    cursor: "pointer",
                    borderRadius: "8px",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = "var(--stone-50)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = "transparent";
                  }}
                >
                  Edit Vehicle
                </button>
                <div style={{ height: "1px", background: "var(--line)", margin: "2px 0" }} />
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowMenu(false);
                    onDelete();
                  }}
                  style={{
                    width: "100%",
                    padding: "10px 16px",
                    border: "none",
                    background: "transparent",
                    textAlign: "left",
                    fontSize: "14px",
                    color: "var(--danger)",
                    cursor: "pointer",
                    borderRadius: "8px",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = "rgba(220, 38, 38, 0.05)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = "transparent";
                  }}
                >
                  Remove Vehicle
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Vehicle details */}
        <div style={{
          display: "flex",
          flexDirection: "column",
          gap: "2px",
        }}>
          <div style={{
            fontSize: "16px",
            fontWeight: 600,
            color: "var(--ink)",
            letterSpacing: "-0.01em",
          }}>
            {vehicle.make} {vehicle.model}
          </div>
          {vehicle.color && (
            <div style={{
              fontSize: "13px",
              color: "var(--stone-500)",
              fontWeight: 500,
            }}>
              {vehicle.color}
            </div>
          )}
        </div>

        {/* Selected indicator */}
        {isSelected && (
          <div style={{
            position: "absolute",
            top: "-8px",
            right: "-8px",
            width: "20px",
            height: "20px",
            borderRadius: "50%",
            background: "var(--blue-500)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: "0 2px 4px rgba(59, 130, 246, 0.2)",
          }}>
            <svg width={12} height={12} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={3}>
              <path d="M5 13l4 4L19 7" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
        )}
      </div>

      {/* Click outside handler for menu */}
      {showMenu && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 99,
          }}
          onClick={() => setShowMenu(false)}
        />
      )}
    </div>
  );
}
